import { useCallback, useMemo, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import dayjs from "dayjs";
import { TriggerGenerateSSO_OLD } from "../features/Feature_SSO/Sso_Types";
import {
  clearSsoSlice,
  triggerGenerateSSOOld,
} from "../features/Feature_SSO/Sso_Slice";
import { useLazyFetchPolicyInsuredQuery } from "../features/Feature_HealthInsurance/HealthInsurance_Api";
import { setError } from "../redux-store/reducers/CsErrorSlice";
import { windowOpen } from "../utils/helper";
import {
  Cs_App_Lob_Id,
  policyRenewalEncryptionIV,
  policyRenewalEncryptionSecretKey,
  policyRenewalSource,
  REACT_APP_URL,
} from "../utils/constants";
import { encryptString } from "../axios/axiosEncryptionUtils";

export const useLobRedirection = () => {
  const [loadingState, setLoadingState] = useState({ key: "", loading: false });
  const ssoSlice = useAppSelector(
    (state) => state.SsoSlice.ssoData as TriggerGenerateSSO_OLD,
  );
  const masterData = useAppSelector(
    (state) => state.GeneralAppElementsSlice.masterData,
  );
  const CsAppSlice = useAppSelector((state) => state.CsAppSlice);
  const HI_policyDetails = useAppSelector(
    (state) => state.HealthInsuranceSlice.allPolicyDetails,
  );
  const dispatch = useAppDispatch();
  const [fetchPolicyInsuredApi] = useLazyFetchPolicyInsuredQuery();

  // Memoize derived values to reduce dependency changes
  const userInfo = useMemo(
    () => ({
      panNo: CsAppSlice.userDetails?.pan,
      name: CsAppSlice.userDetails?.name,
      dob: CsAppSlice.userDetails?.dob,
      email: "" + CsAppSlice.userDetails?.emailId,
      mobile: "" + CsAppSlice.userDetails?.mobileNumber,
    }),
    [
      CsAppSlice.userDetails?.pan,
      CsAppSlice.userDetails?.name,
      CsAppSlice.userDetails?.dob,
      CsAppSlice.userDetails?.emailId,
      CsAppSlice.userDetails?.mobileNumber,
    ],
  );

  const masterDetail = useMemo(
    () => masterData?.masterDetail,
    [masterData?.masterDetail],
  );

  const redirectToLob = useCallback(
    (
      uniqeuId: string,
      functionalityCode: string,
      lobName: string,
      redirectionUrl: string,
      fromAdaptive: boolean,
    ) => {
      const lobDetails = masterData?.productCategory.find(
        (f) =>
          f.lob === lobName || f.lobName === lobName || f.lobId === lobName,
      );
      const customerAndLobDetails =
        CsAppSlice.userDetails?.customerAndLobDetails?.find(
          (f) =>
            f.PRIMARY_SECONDARY_USER === "primary" && f.LOB === lobDetails?.lob,
        );

      let networkHospitalRedirectionUrl =
        masterDetail?.["network-hospital"]?.[0].masterValue;

      let callADoctorRedirectionUrl =
        masterDetail?.["call-a-doctor"]?.[0].masterValue;

      const ciiNo =
        customerAndLobDetails?.CII ||
        CsAppSlice.userDetails?.customerAndLobDetails?.[0]?.CII;
      const clientId = customerAndLobDetails?.LOBID || "";

      if (functionalityCode === "M_BMI") {
        const policyNumber = HI_policyDetails[0].PolicyNumber || "";
        setLoadingState(() => {
          return { key: uniqeuId, loading: true };
        });
        fetchPolicyInsuredApi(policyNumber).then((res) => {
          if (res.isError && res.error) {
            setLoadingState(() => {
              return { key: uniqeuId, loading: false };
            });
            dispatch(
              setError({
                error: new Error("Error"),
                message: "Unable to Navigate!",
              }),
            );
            return;
          }
          if (res.data?.code === 1 && res?.data?.payload?.Response) {
            setLoadingState(() => {
              return { key: uniqeuId, loading: false };
            });
            const customerCode =
              res?.data?.payload?.Response.PolicyDetail.CustomerCode;

            const bmiCalculatorRedirectionUrl = redirectionUrl.replace(
              "customerCode",
              customerCode || "",
            );
            dispatch(clearSsoSlice());
            windowOpen(bmiCalculatorRedirectionUrl, "_self");
          }
        });
        return;
      }
      if (functionalityCode === "M_FNH") {
        fromAdaptive
          ? windowOpen(
              networkHospitalRedirectionUrl,
              "_self",
              "menubar=no,toolbar=no",
            )
          : windowOpen(networkHospitalRedirectionUrl, "_self");
        return;
      }

      if (functionalityCode === "CAD") {
        windowOpen(callADoctorRedirectionUrl, "_self");
        return;
      }

      if (functionalityCode === "PR") {
        const policyRenewalRedirectionUrl = redirectionUrl
          .replace(
            "policyNum",
            encodeURIComponent(
              encryptString(
                HI_policyDetails[0].PolicyNumber || "",
                policyRenewalEncryptionIV || "",
                policyRenewalEncryptionSecretKey,
              ),
            ),
          )
          .replace(
            "mobileNumber",
            encodeURIComponent(
              encryptString(
                userInfo.mobile || "",
                policyRenewalEncryptionIV || "",
                policyRenewalEncryptionSecretKey,
              ),
            ),
          )
          .replace(
            "dob",
            encodeURIComponent(
              encryptString(
                dayjs(dayjs(userInfo.dob).format("YYYY/MM/DD")).format(
                  "DD/MM/YYYY",
                ) || "",
                policyRenewalEncryptionIV || "",
                policyRenewalEncryptionSecretKey,
              ),
            ),
          )
          .replace(
            "source",
            encodeURIComponent(
              encryptString(
                policyRenewalSource || "ABC App",
                policyRenewalEncryptionIV || "",
                policyRenewalEncryptionSecretKey,
              ),
            ),
          );
        dispatch(clearSsoSlice());
        windowOpen(policyRenewalRedirectionUrl, "_self");
      } else {
        const oldTriggerValue = (ssoSlice as TriggerGenerateSSO_OLD)
          .triggerGenerateSSO_Old;
        const ssoObj = {
          triggerGenerateSSO_Old: oldTriggerValue ? !oldTriggerValue : true,
          redirectionUrl: redirectionUrl,
          ciiNo: ciiNo,
          functionality: functionalityCode,
          mobileNo: userInfo.mobile,
          lobClientId: clientId,
          channel: CsAppSlice.channel,
          sourceLobId: Cs_App_Lob_Id,
          destinationLobId: lobDetails?.lobId,
          callbackUrl: `${REACT_APP_URL}/login`,
          appVersion: "1",
          panNumber: userInfo.panNo,
          customerFullName: userInfo.name,
          customerDob: userInfo.dob,
          emailId: userInfo.email,
          osVersion: "Windows",
          imei: "1231312",
          deviceId: "131312",
          userAgent: navigator.userAgent,
          clientIpAddress: "",
          udp: "",
          udp2: clientId,
          udp3: "",
          udp4: "",
          udp5: "",
          udp6: "",
          udp7: "",
          udp8: "",
          udp9: "",
          udp10: "",
          showLoading: true,
        } as TriggerGenerateSSO_OLD;
        if (lobDetails?.lobId === "06") {
          ssoObj.userId = "OneABCTest";
          ssoObj.apiPassword = "T25lQUJDVGVzdDEyMw==";
        }
        dispatch(triggerGenerateSSOOld(ssoObj));
      }
    },
    [
      ssoSlice,
      dispatch,
      masterData,
      CsAppSlice,
      HI_policyDetails,
      userInfo,
      masterDetail,
      fetchPolicyInsuredApi,
    ],
  );

  return { redirectToLob, loadingState };
};
